import "@/App.css";
import "./i18n";
import { useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { 
  Hand, Megaphone, Globe, Sparkles, Mail, CheckCircle, ArrowRight,
  Trophy, Heart, BookOpen, Users, Clock, Download
} from "lucide-react";
import confetti from 'canvas-confetti';
import { Toaster, toast } from 'sonner';
import { ThemeProvider, useTheme } from "./lib/theme";
import { PrivacyPage, TermsPage } from "./pages/LegalPages";
import { CookiesPage } from "./pages/CookiesPage";

// Import components
import { Header } from "./components/Header";
import { HeroSection } from "./components/Hero";
import { Footer } from "./components/Footer";
import { CookieConsent } from "./components/CookieConsent";

const PORTAL_SIGNUP_URL = "https://portal.teacherhappy.com/signup";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

// Our Method Section
const MethodSection = () => {
  const { t } = useTranslation();

  const features = [
    { icon: Hand, titleKey: 'tprTitle', descKey: 'tprDesc', color: "icon-yellow" },
    { icon: Megaphone, titleKey: 'vocalTitle', descKey: 'vocalDesc', color: "icon-blue" },
    { icon: Globe, titleKey: 'globalTitle', descKey: 'globalDesc', color: "icon-coral" }
  ];

  return (
    <section id="method" data-testid="method-section" className="py-20 md:py-28" style={{ backgroundColor: 'var(--th-background)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer} className="text-center mb-16">
          <motion.span variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-4" style={{ backgroundColor: 'rgba(255, 215, 0, 0.2)', color: '#D97706' }}>
            <Trophy className="w-4 h-4" /> {t('method.badge')}
          </motion.span>
          <motion.h2 variants={fadeInUp} className="font-heading text-4xl md:text-5xl font-bold mb-4" style={{ color: 'var(--th-text-primary)' }}>{t('method.title')}</motion.h2>
          <motion.p variants={fadeInUp} className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--th-text-secondary)' }}>{t('method.subtitle')}</motion.p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={staggerContainer} className="grid md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div key={index} variants={fadeInUp} className="card-feature group" data-testid={`feature-card-${index}`}>
              <div className={`icon-container ${feature.color} mb-6`}><feature.icon className="w-8 h-8" /></div>
              <h3 className="font-heading text-2xl font-bold mb-3" style={{ color: 'var(--th-text-primary)' }}>{t(`method.${feature.titleKey}`)}</h3>
              <p style={{ color: 'var(--th-text-secondary)' }} className="leading-relaxed">{t(`method.${feature.descKey}`)}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Roadmap Section
// Why Teacher Happy Section
const WhyTeacherHappySection = () => {
  const { t } = useTranslation();

  const benefits = [
    { icon: Users, titleKey: 'native', color: '#2563EB' },
    { icon: Globe, titleKey: 'schedule', color: '#059669' },
    { icon: Trophy, titleKey: 'progress', color: '#D97706' },
    { icon: Heart, titleKey: 'flashcards', color: '#DC2626' },
    { icon: BookOpen, titleKey: 'books', color: '#7C3AED' },
    { icon: Clock, titleKey: 'recordings', color: '#0891B2' },
    { icon: Sparkles, titleKey: 'more', color: '#F59E0B' },
  ];

  return (
    <section id="why-us" data-testid="why-us-section" className="py-20 md:py-28 section-light-blue">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer} className="text-center mb-16">
          <motion.span variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-4" style={{ backgroundColor: 'rgba(0, 122, 255, 0.2)', color: '#2563EB' }}>
            <CheckCircle className="w-4 h-4" /> {t('whyUs.badge')}
          </motion.span>
          <motion.h2 variants={fadeInUp} className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold mb-4" style={{ color: 'var(--th-text-primary)' }}>{t('whyUs.title')}</motion.h2>
          <motion.p variants={fadeInUp} className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--th-text-secondary)' }}>{t('whyUs.subtitle')}</motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
          {benefits.map((benefit, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="p-6 rounded-2xl shadow-lg border-2 hover:shadow-xl transition-all hover:-translate-y-1"
              style={{ backgroundColor: 'var(--th-card-bg)', borderColor: 'var(--th-card-border)' }}
              data-testid={`benefit-${index}`}
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: `${benefit.color}15` }}
              >
                <benefit.icon className="w-6 h-6" style={{ color: benefit.color }} />
              </div>
              <h3 className="font-heading text-lg font-bold" style={{ color: 'var(--th-text-primary)' }}>
                {t(`whyUs.${benefit.titleKey}`)}
              </h3>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Lead Magnet Section
const LeadMagnetSection = () => {
  const { t } = useTranslation();
  
  // Direct R2 URL for the PDF guide
  const FREE_SAMPLE_URL = 'https://assets.teacherhappy.com/my-world-guide.pdf';

  return (
    <section id="lead-magnet" data-testid="lead-magnet-section" className="py-20 md:py-28 section-warm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="relative order-2 lg:order-1">
            <div className="flashcard-3d">
              <img src="https://customer-assets.emergentagent.com/job_edu-landing-16/artifacts/ivjr20kn_image.png" alt="My World - A Vocal-Kinesthetic Adventure" className="w-full h-auto rounded-3xl shadow-2xl" data-testid="flashcard-image" />
            </div>
            <motion.div animate={{ rotate: [0, 5, 0, -5, 0] }} transition={{ duration: 4, repeat: Infinity }} className="absolute -top-6 -right-6 rounded-2xl p-4 shadow-xl" style={{ backgroundColor: 'var(--th-primary)' }}>
              <span className="font-heading font-bold text-lg" style={{ color: '#1E293B' }}>{t('leadMagnet.free')}</span>
            </motion.div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="order-1 lg:order-2">
            <motion.span variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-4" style={{ backgroundColor: 'rgba(255, 107, 107, 0.2)', color: '#DC2626' }}>{t('leadMagnet.badge')}</motion.span>
            <motion.h2 variants={fadeInUp} className="font-heading text-4xl md:text-5xl font-bold mb-6" style={{ color: 'var(--th-text-primary)' }}>{t('leadMagnet.title1')}<br /><span style={{ color: 'var(--th-secondary)' }}>{t('leadMagnet.title2')}</span> {t('leadMagnet.title3')}</motion.h2>
            <motion.p variants={fadeInUp} className="text-lg mb-6" style={{ color: 'var(--th-text-secondary)' }}>{t('leadMagnet.subtitle')}</motion.p>
            <motion.ul variants={fadeInUp} className="space-y-3 mb-8">
              {['feature1', 'feature2', 'feature3', 'feature4'].map((key, i) => (
                <li key={i} className="flex items-center gap-3" style={{ color: 'var(--th-text-secondary)' }}><CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />{t(`leadMagnet.${key}`)}</li>
              ))}
            </motion.ul>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-3">
              <a 
                href={FREE_SAMPLE_URL}
                target="_blank"
                rel="noopener noreferrer"
                download="my-world-guide.pdf"
                className="btn-secondary inline-flex items-center justify-center gap-2"
                data-testid="download-sample-btn"
              >
                <Download className="w-5 h-5" />
                {t('leadMagnet.downloadSample') || 'Download Free Sample'}
              </a>
              <a href="#hero" className="btn-primary inline-flex items-center justify-center gap-2" data-testid="lead-magnet-cta">
                {t('leadMagnet.cta')} <ArrowRight className="w-5 h-5" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const { t } = useTranslation();

  return (
    <section id="about" data-testid="about-section" className="py-20 md:py-28" style={{ backgroundColor: 'var(--th-background)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
            <motion.span variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-4" style={{ backgroundColor: 'rgba(0, 122, 255, 0.2)', color: '#2563EB' }}><Heart className="w-4 h-4" /> {t('about.badge')}</motion.span>
            <motion.h2 variants={fadeInUp} className="font-heading text-4xl md:text-5xl font-bold mb-6" style={{ color: 'var(--th-text-primary)' }}>{t('about.title1')}<br /><span style={{ color: 'var(--th-primary)' }}>{t('about.title2')}</span></motion.h2>
            <motion.div variants={fadeInUp} className="space-y-4 text-lg" style={{ color: 'var(--th-text-secondary)' }}>
              <p>{t('about.p1')} <strong style={{ color: 'var(--th-text-primary)' }}>{t('about.p1Bold')}</strong></p>
              <p>{t('about.p2')}</p>
              <p>{t('about.p3')}</p>
            </motion.div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="relative">
            <img src="https://images.unsplash.com/photo-1587616211892-f743fcca64f9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIweW91bmclMjBjaGlsZHJlbiUyMGxlYXJuaW5nJTIwZW5nbGlzaCUyMHNjaG9vbCUyMGhhcHB5fGVufDB8fHx8MTc3MzQ1MzU5Nnww&ixlib=rb-4.1.0&q=85" alt="Happy young children learning together" className="w-full h-auto rounded-3xl shadow-2xl" data-testid="about-image" />
            <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 3, repeat: Infinity }} className="absolute -bottom-6 -left-6 rounded-2xl p-4 shadow-xl border-2" style={{ backgroundColor: 'var(--th-card-bg)', borderColor: 'var(--th-card-border)' }}>
              <div className="text-center"><span className="font-heading text-2xl font-bold" style={{ color: 'var(--th-secondary)' }}>✋</span><p className="text-sm font-semibold" style={{ color: 'var(--th-text-primary)' }}>{t('about.kinesthetic')}</p></div>
            </motion.div>
            <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 4, repeat: Infinity }} className="absolute -top-6 -right-6 rounded-2xl p-4 shadow-xl" style={{ backgroundColor: 'var(--th-secondary)' }}>
              <div className="text-center text-white"><span className="font-heading text-3xl font-bold">TPR</span><p className="text-sm opacity-90">{t('about.basedLearning')}</p></div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  const { t } = useTranslation();
  const { currentTheme } = useTheme();
  const [formData, setFormData] = useState({ full_name: '', email: '', wechat_id: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.full_name || isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      // Mock submission - no database needed
      await new Promise(resolve => setTimeout(resolve, 800));
      console.log('Form submitted (mock):', formData);
      
      setIsSubmitted(true);
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 }, colors: ['#FFD700', '#007AFF', '#FF6B6B'] });
      toast.success(t('toast.success') || "Ready to Learn? 🎉");
      
      // Build URL with parameters for portal handshake
      const params = new URLSearchParams({
        name: formData.full_name,
        email: formData.email,
        theme: currentTheme
      });
      if (formData.wechat_id) {
        params.append('wechat', formData.wechat_id);
      }
      
      // Redirect to Portal signup with pre-filled data
      setTimeout(() => { 
        window.location.href = `${PORTAL_SIGNUP_URL}?${params.toString()}`; 
      }, 3000);
    } catch (error) { 
      toast.error(t('toast.error') || "Something went wrong. Please try again."); 
      console.error('Submission error:', error);
    }
    finally { setIsSubmitting(false); }
  };

  return (
    <section id="signup" data-testid="cta-section" className="py-20 md:py-28 cta-gradient relative overflow-hidden">
      <div className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-400/10 rounded-full blur-3xl"></div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
          <motion.h2 variants={fadeInUp} className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">{t('cta.title1')}<br /><span style={{ color: 'var(--th-primary)' }}>{t('cta.title2')}</span></motion.h2>
          <motion.p variants={fadeInUp} className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">{t('cta.subtitle')}</motion.p>
          <motion.form variants={fadeInUp} onSubmit={handleSubmit} className="flex flex-col gap-4 max-w-lg mx-auto" data-testid="cta-form">
            {!isSubmitted ? (
              <>
                <input 
                  type="text" 
                  name="full_name"
                  value={formData.full_name} 
                  onChange={handleInputChange} 
                  placeholder={t('hero.namePlaceholder') || "Full Name"} 
                  className="px-6 py-4 rounded-full border-2 border-white/20 bg-white/10 backdrop-blur text-white placeholder:text-blue-200 outline-none focus:border-white/50 transition-all text-lg" 
                  required 
                  data-testid="cta-name-input" 
                />
                <input 
                  type="email" 
                  name="email"
                  value={formData.email} 
                  onChange={handleInputChange} 
                  placeholder={t('hero.emailPlaceholder')} 
                  className="px-6 py-4 rounded-full border-2 border-white/20 bg-white/10 backdrop-blur text-white placeholder:text-blue-200 outline-none focus:border-white/50 transition-all text-lg" 
                  required 
                  data-testid="cta-email-input" 
                />
                <input 
                  type="text" 
                  name="wechat_id"
                  value={formData.wechat_id} 
                  onChange={handleInputChange} 
                  placeholder={t('hero.wechatPlaceholder') || "WeChat ID (optional)"} 
                  className="px-6 py-4 rounded-full border-2 border-white/20 bg-white/10 backdrop-blur text-white placeholder:text-blue-200 outline-none focus:border-white/50 transition-all text-lg" 
                  data-testid="cta-wechat-input" 
                />
                <button type="submit" className="btn-primary whitespace-nowrap" disabled={isSubmitting} data-testid="cta-submit-button">
                  {isSubmitting ? t('hero.submitting') : t('hero.submitButton')}
                </button>
              </>
            ) : (
              <div className="flex items-center justify-center gap-3 bg-white/20 backdrop-blur rounded-full px-6 py-4 text-white font-semibold mx-auto" data-testid="cta-success-message">
                <CheckCircle className="w-6 h-6" />{t('hero.successMessage') || "Ready to Learn? 🎉 Redirecting..."}
              </div>
            )}
          </motion.form>
        </motion.div>
      </div>
    </section>
  );
};

// Landing Page
const LandingPage = () => (
  <>
    <HeroSection />
    <MethodSection />
    <WhyTeacherHappySection />
    <LeadMagnetSection />
    <AboutSection />
    <CTASection />
  </>
);

// Main App
function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <div className="App">
          <Toaster position="top-center" richColors />
          <Header />
          <main>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/cookies" element={<CookiesPage />} />
            </Routes>
            <CookieConsent />
          </main>
          <Footer />
        </div>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
